import { useMemo, useState } from 'react';
import { Button, Card, Choice, Slider, useToast } from '@efira/ui';

import { useAffiliate, useAffiliateReward, useCreateAffiliateLink } from '../../api/queries';
import { deviceId } from '../../lib/device';
import { plural } from '../../lib/plural';

/**
 * Партнёрская программа: наше сделано целиком, заглушка одна - вывод денег.
 *
 * ЧТО РАБОТАЕТ ПРЯМО СЕЙЧАС: ссылка, переходы, закрепление автора при
 * регистрации, счётчики, выбор дорожки, начисления с каждого платежа и окно
 * возвратов. Начисления копятся уже сегодня - чтобы к моменту подключения
 * оплаты у партнёра была история, а не ноль.
 *
 * ЧЕГО НЕТ И НЕ ОБЕЩАНО: кнопки вывода, реквизитов, названия канала выплат и
 * срока перевода. Мы не контролируем ни один из них, пока нет провайдера, а
 * обещание срока чужих денег - худшее, что можно написать на этом экране.
 *
 * ЧЕГО НЕТ ИЗ ЭТАЛОНА: лозунгов «No minimum audience», «Every click tracked»,
 * «Renewals included». Два из трёх у нас правда, но лозунг - это реклама, а
 * на экране про чужие деньги нужны условия. Вместо них три шага.
 */

/** Числа считаются из условий, пришедших с сервера. Ни одного в разметке. */
function money(minor: number): string {
  const whole = Math.trunc(minor / 100);
  const rest = Math.abs(minor % 100);

  return rest === 0 ? `${String(whole)} $` : `${String(whole)},${String(rest).padStart(2, '0')} $`;
}

const STEPS = [
  {
    title: 'Создайте ссылку',
    text: 'Одна ссылка на аккаунт. Она не меняется и работает всегда.',
  },
  {
    title: 'Поделитесь ею',
    text: 'В описании канала, в ролике, в чате — где угодно. Переходы считаются с первого дня.',
  },
  {
    /* «Копите начисления», а не «получайте отчисления»: получать пока
       негде, а слово одно на весь экран - начисление. */
    title: 'Копите начисления',
    text: 'С первого платежа приведённого автора и дальше, пока он платит.',
  },
];

export function Affiliate() {
  const device = useMemo(() => deviceId(), []);
  const affiliate = useAffiliate(device);
  const create = useCreateAffiliateLink();
  const reward = useAffiliateReward();
  const toast = useToast();
  const [authors, setAuthors] = useState(5);

  const data = affiliate.data;
  /* Ответа ещё нет: показывать нули в кабинете о деньгах нельзя. */
  const waiting = data === undefined;
  const terms = data?.terms;
  const share = terms?.sharePercent ?? 0;
  const monthPrice = terms?.monthPriceMinor ?? 0;
  /* Доход считается из ЦЕНЫ и СТАВКИ, пришедших с сервера: оба числа живут
     в одном месте (`world/pricing.ts`), и разметка их не знает. */
  const perAuthorMinor = Math.floor((monthPrice * share) / 100);
  const monthlyMinor = perAuthorMinor * authors;

  return (
    <div data-affiliate="root" className="flex flex-col gap-5">
      {/* УСЛОВИЯ - `affiliate.hero`. Наши, а не пересказ чужих. */}
      <section
        data-affiliate="hero"
        className="border-primary/40 bg-muted rounded-lg flex flex-col gap-2 border p-4"
      >
        {/* Главное обещание экрана - самое крупное на нём. Заголовок окна
            служебный, а это - то, ради чего человек сюда пришёл. */}
        {/*
          АКЦЕНТ - ЗАЛИВКОЙ, А НЕ ЦВЕТОМ ТЕКСТА (решение владельца
          2026-09-26). `text-primary` в тёмной теме давал 1,95:1 при
          требуемых 3:1 для крупного текста: главное число страницы почти
          не читалось (аудит единицы 16, находка 17).
        */}
        <h3 className="text-foreground text-2xl font-semibold">
          <span className="bg-primary text-primary-foreground rounded-md px-2 py-1">
            {String(share)} %
          </span>{' '}
          с каждого платежа, пока автор платит
        </h3>
        {/* Не «пожизненно» в отрыве от условия: заголовок и тело обязаны
            говорить одно и то же (находка разбора текстов). */}
        <p className="text-foreground/90 text-sm">
          Приведённый автор закрепляется за вами навсегда, и срока у начислений нет: они идут с
          каждого его платежа, пока жива подписка, — а не 12 месяцев, как принято.
        </p>
        {/* База начисления названа прямо: это про ЕГО деньги. */}
        <p data-affiliate="base" className="text-foreground/80 text-sm">
          Считаем с суммы, которая дошла до нас после комиссии платёжного сервиса и налога, — не с
          цены в прайсе. Начисление можно выплачивать через {String(terms?.holdDays ?? 0)} дней: это
          окно возвратов. Возврат платежа снимает начисление, и вы это увидите здесь.
        </p>
      </section>

      {/* ТРИ ШАГА - `affiliate.step`. Вместо лозунгов эталона. */}
      <section className="grid gap-3 @md:grid-cols-3">
        {STEPS.map((step, index) => (
          <Card key={step.title} data-affiliate-step={index + 1} size="sm" className="gap-1">
            <span
              data-affiliate="badge"
              className="border-foreground/20 text-foreground/90 flex size-6 items-center justify-center rounded-full border text-xs"
            >
              {index + 1}
            </span>
            <span className="text-foreground text-sm font-medium">{step.title}</span>
            <span className="text-foreground/80 text-xs">{step.text}</span>
          </Card>
        ))}
      </section>

      {/* ССЫЛКА. */}
      <Card
        data-affiliate="link-block"
        data-control-row="affiliate-link"
        size="sm"
        className="flex-row flex-wrap items-center gap-3"
      >
        {data?.link === null || data?.link === undefined ? (
          <>
            <span className="text-foreground/90 min-w-0 flex-1 text-sm">
              Ссылки ещё нет. Создадим — и переходы начнут считаться сразу.
            </span>
            <Button
              variant="primary"
              size="s"
              data-affiliate="cta"
              loading={create.isPending}
              onClick={() => {
                create.mutate(device, {
                  onSuccess: () => {
                    toast.show({
                      tone: 'success',
                      title: 'Ссылка создана',
                      /* НЕ «поделитесь один раз»: делиться можно сколько
                         угодно, не меняется сама ссылка. Прежний текст
                         читался как ограничение (находка владельца). */
                      text: 'Ссылка постоянная: её можно публиковать где угодно и сколько угодно.',
                    });
                  },
                });
              }}
            >
              Создать партнёрскую ссылку
            </Button>
          </>
        ) : (
          <>
            <output
              data-affiliate="link"
              aria-label="Партнёрская ссылка"
              className="block min-w-0 flex-1 truncate font-mono text-xs"
            >
              {data.link}
            </output>
            <Button
              variant="primary"
              size="s"
              data-affiliate="copy"
              onClick={() => {
                void navigator.clipboard
                  .writeText(data.link ?? '')
                  .then(() => {
                    toast.show({ tone: 'success', title: 'Ссылка скопирована' });
                  })
                  .catch(() => {
                    toast.show({
                      tone: 'warning',
                      title: 'Браузер не дал доступ к буферу',
                      text: 'Скопируйте ссылку вручную — она видна целиком.',
                    });
                  });
              }}
            >
              Копировать
            </Button>
          </>
        )}
      </Card>

      {/*
        СЧЁТЧИКИ. Показывается только то, что мы действительно считаем.

        ДО ОТВЕТА СЕРВЕРА СТОИТ «…», А НЕ НОЛЬ. Тот же дефект, что пустая
        сетка падов во время загрузки: ноль в кабинете партнёра читается как
        «переходов нет», а это чужие деньги. Ноль показывается только тогда,
        когда он настоящий.
      */}
      <section data-affiliate="stats" className="grid grid-cols-2 gap-3 @md:grid-cols-4">
        {[
          { id: 'clicks', title: 'Переходы', value: waiting ? '…' : String(data?.stats.clicks) },
          {
            id: 'signups',
            title: 'Регистрации',
            value: waiting ? '…' : String(data?.stats.signups),
          },
          {
            id: 'paying',
            title: 'Из них платят',
            value: waiting ? '…' : String(data?.stats.paying),
          },
          {
            id: 'available',
            /* Одна строка: «доступно к выплате» переносилось и роняло ряд
               чисел с общей базовой линии. */
            title: 'Готово к выплате',
            value: waiting ? '…' : money(data?.stats.availableMinor ?? 0),
          },
        ].map((cell) => (
          <Card key={cell.id} data-affiliate-stat={cell.id} size="sm" className="gap-1">
            <span className="text-foreground/80 text-xs tracking-wide uppercase">{cell.title}</span>
            <span className="text-foreground text-xl font-semibold tabular-nums">{cell.value}</span>
          </Card>
        ))}
      </section>

      {/* Молчание о том, что выплат ещё нет, рядом с суммой в долларах -
          это обещание счёта, с которого можно снять. Говорим прямо. */}
      <p data-affiliate="payout-state" className="text-foreground/90 text-sm">
        Переводы пока не подключены: начисления копятся и не сгорают.
      </p>

      {(data?.stats.heldMinor ?? 0) > 0 && (
        <p data-affiliate="held" className="text-foreground/90 text-sm">
          Ещё {money(data?.stats.heldMinor ?? 0)} удерживается до конца окна возвратов.
        </p>
      )}

      {(data?.stats.reversedMinor ?? 0) > 0 && (
        /* Снятое возвратом не прячется: молча пропавшая строка - это спор,
           в котором партнёру нечего показать. */
        <p data-affiliate="reversed" className="text-foreground/90 text-sm">
          Снято возвратами: {money(data?.stats.reversedMinor ?? 0)}.
        </p>
      )}

      {/* ДОРОЖКА: деньги или месяцы Pro. */}
      <Card data-affiliate="reward" size="sm" className="gap-2">
        <span className="text-foreground text-sm font-semibold">Как получать</span>
        <Choice<'money' | 'months'>
          label="Как получать"
          value={data?.reward === 'months' ? 'months' : 'money'}
          onChange={(value) => {
            reward.mutate(value, {
              onSuccess: (result) => {
                toast.show({
                  tone: result.ok ? 'success' : 'warning',
                  title: result.ok ? 'Дорожка изменена' : 'Пока нельзя',
                  ...(result.message === undefined ? {} : { text: result.message }),
                });
              },
            });
          }}
          size="sm"
          options={(
            [
              { value: 'money', label: 'Деньгами', data: { 'data-affiliate-reward': 'money' } },
              {
                value: 'months',
                label: 'Месяцами Pro',
                data: { 'data-affiliate-reward': 'months' },
              },
            ] as const
          ).map((option) => ({ ...option, disabled: reward.isPending }))}
        />
        <p className="text-foreground/80 text-sm">
          Месяцами: месяц Pro за каждый платёж приведённого автора. Дорожка применяется к новым
          начислениям — уже удерживаемые останутся на прежней. Менять можно раз в 30 дней.
        </p>
      </Card>

      {/* КАЛЬКУЛЯТОР - `affiliate.calc-slider` и `affiliate.calc-out`. */}
      <Card data-affiliate="calc" className="gap-3">
        <label className="flex flex-col gap-1 text-sm" htmlFor="affiliate-authors">
          Сколько авторов приведено: <b className="tabular-nums">{String(authors)}</b>
          <Slider
            id="affiliate-authors"
            data-affiliate="calc-slider"
            aria-label="Сколько авторов приведено"
            min={1}
            max={500}
            value={[authors]}
            onValueChange={(next) => {
              setAuthors(next[0] ?? 1);
            }}
          />
        </label>

        <div data-affiliate="calc-out" className="flex flex-col gap-1">
          {data?.reward === 'months' ? (
            /* Для дорожки «месяцы» доллары не показываются вовсе: человек
               выбрал не их, и сумма рядом читалась бы как обещание. */
            <>
              <span className="text-foreground text-2xl font-semibold">
                {plural(authors, ['месяц', 'месяца', 'месяцев'])} Pro
              </span>
              <span className="text-foreground/80 text-sm">
                По месяцу за каждого приведённого платящего автора.
              </span>
            </>
          ) : (
            <>
              <span className="text-foreground text-2xl font-semibold tabular-nums">
                около {money(monthlyMinor)} в месяц
              </span>
              {/*
                ОРИЕНТИР, А НЕ ОБЕЩАНИЕ.

                Здесь считается доля от ЦЕНЫ, а начисляется - от суммы
                платежа за вычетом чужих удержаний; на годовом тарифе платёж
                один в год. Годовая проекция убрана вовсе: 500 авторов × 12
                месяцев - это прогноз удержания подписчиков, которого у нас
                нет (находка разбора текстов).
              */}
              <span className="text-foreground/80 text-sm">
                {money(perAuthorMinor)} с автора — это {String(share)} % от {money(monthPrice)} в
                месяц. Ориентир: фактическое начисление меньше на комиссию платёжного сервиса и
                налог, а при годовой оплате распределяется на год.
              </span>
            </>
          )}
        </div>
      </Card>

      {/* ВЫПЛАТЫ - `affiliate.payout`. Единственная заглушка контура. */}
      <section
        data-affiliate="payout"
        className="border-foreground/15 rounded-lg flex flex-col gap-2 border border-dashed p-3"
      >
        <span className="text-foreground text-sm font-semibold">Выплаты</span>
        <p className="text-foreground/90 text-sm">
          Переводы включим вместе с оплатой: сейчас списаний в продукте нет, и выводить нечего.
          Механизм начислений при этом уже работает — как пойдут платежи, они начнут считаться с
          первого, и история не начнётся с нуля.
        </p>
        {/* НИ СРОКА, НИ КАНАЛА: ни тем, ни другим мы сейчас не управляем, а
            «раз в месяц» на экране читается как обязательство. */}
        <p className="text-foreground/80 text-sm">
          Когда переводы заработают, рассчитываем платить от {terms?.thresholdText ?? '—'} и не реже
          чем {terms?.payoutPeriod ?? ''}. Точные сроки и канал назовём вместе с подключением
          платёжного сервиса — обещать их сейчас нечестно.
        </p>
        <p data-affiliate="terms-version" className="text-foreground/80 text-sm">
          Условия версии {terms?.version ?? '—'}. Если изменим их, уже приведённые авторы останутся
          на прежних: ставка хранится вместе с закреплением.
        </p>
      </section>

      {/* НАЧИСЛЕНИЯ. Пустое состояние объясняет, когда появится первая строка. */}
      <section data-affiliate="accruals" className="flex flex-col gap-2">
        <h3 className="text-foreground text-sm font-semibold">Начисления</h3>

        {(data?.accruals.length ?? 0) === 0 ? (
          <p
            data-affiliate="accruals-empty"
            className="border-foreground/10 rounded-lg text-foreground/80 border border-dashed p-3 text-sm"
          >
            Первая строка появится, когда приведённый вами автор оплатит Pro. Пока оплата не
            подключена, платежей нет ни у кого — и начислять не с чего.
          </p>
        ) : (
          <ul className="flex flex-col gap-1">
            {(data?.accruals ?? []).map((row) => (
              <li
                key={row.id}
                data-affiliate-accrual={row.id}
                className="border-foreground/10 grid grid-cols-[7rem_5rem_1fr] items-center gap-3 border-b py-2 text-sm last:border-b-0"
              >
                <span className="text-foreground/90 tabular-nums">
                  {new Date(row.at).toLocaleDateString('ru-RU')}
                </span>
                <span className="text-foreground font-medium tabular-nums">{row.text}</span>
                <span className="text-foreground/80">
                  {row.status === 'held' && row.heldUntil !== null
                    ? `удерживается до ${new Date(row.heldUntil).toLocaleDateString('ru-RU')}`
                    : row.status === 'available'
                      ? 'можно выплачивать'
                      : row.status === 'granted'
                        ? 'выдан месяц Pro'
                        : 'снято возвратом'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
